/**
 * Canonical catalog of analytics event names.
 *
 * Keep names in the `domain:action` shape so they group cleanly in Clarity.
 * Custom events carry a name only; pass contextual IDs and counts (lessonId,
 * enrollmentId, gradeCount, etc.) as the `properties` argument to
 * `AnalyticsService.trackEvent` and they are recorded as filterable session tags.
 */
export const AnalyticsEvent = {
	// Instructor actions.
	// Grades, objectives, project milestones and homework are all edited in the
	// lesson-detail form and persisted together in a single save — there is no
	// discrete "add a grade" moment. So a completed review is one event, and the
	// property tags describe what it contained (gradeCount, objectiveCount, etc.).
	LessonReviewSaved: 'instructor:lesson_review_saved',
	// A pre-lesson draft save: the same detail content, but no attendance and no completion. Kept
	// distinct from LessonReviewSaved so the two can be counted separately — the ratio is how we
	// measure whether instructors actually plan lessons ahead.
	LessonDraftSaved: 'instructor:lesson_draft_saved',
	FinalReviewSaved: 'instructor:final_review_saved',
	LessonMarkedAbsent: 'instructor:lesson_marked_absent',

	// Lesson actions available to both instructors and learners (role tag disambiguates).
	LessonJoined: 'lesson:joined',
	LessonRescheduled: 'lesson:rescheduled',

	// Survey actions (shared component — used by both learners and instructors,
	// disambiguated by the respondentType tag).
	SurveyStarted: 'survey:started',
	SurveySubmitted: 'survey:submitted',

	// Shared components
	VoiceRecordStarted: 'transcription:voice_record_started',

	// Learner onboarding funnel (pre-auth). Distinct event per step so Clarity funnels can
	// measure drop-off between steps directly; the failure events explain why users drop.
	OnboardingWelcomeViewed: 'onboarding:welcome_viewed',
	OnboardingUsernameViewed: 'onboarding:username_viewed',
	OnboardingVerifyViewed: 'onboarding:verify_viewed',
	OnboardingPasswordViewed: 'onboarding:password_viewed',
	OnboardingCompleted: 'onboarding:completed',
	OnboardingAlreadyComplete: 'onboarding:already_complete',
	OnboardingError: 'onboarding:error',
	OnboardingCodeRejected: 'onboarding:code_rejected'
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];
